<template>
    <div class="d-flex justify-center align-center fill-height page">
        <v-container class="d-flex justify-center align-center flex-column">
            <v-card flat class="form-wrapper pa-8 text-center" :key="1">
                <v-form ref="codeForm" @submit.prevent="verifyEmailAction">
                    <!-- Lock Icon -->
                    <div class="lock-icon-wrapper mb-8">
                        <div class="lock-icon-circle">
                            <v-icon color="white" size="48">mdi-lock-outline</v-icon>
                        </div>
                    </div>

                    <!-- Title -->
                    <h1 class="confirm-title mb-6">Confirm Your Email</h1>
                    
                    <!-- Subtitle with Email -->
                    <p class="confirm-subtitle mb-2">
                        Enter the 4-digit code we just sent to<br>
                        your email <span class="user-email">{{ user.email }}</span>.
                    </p>
                    <p class="mb-8">
                        <span class="not-your-email">Not your email? </span>
                        <a href="#" class="change-email-link" @click.prevent="showChangeEmailDialog = true">Change it</a>
                    </p>
                    
                    <!-- Digit Inputs -->
                    <digits-input color="#D4780A" :loading="loading" :auto-focus="true" @change="updateCode" class="mb-10" />
                    
                    <!-- Verify Button -->
                    <v-btn 
                        type="submit" 
                        block 
                        depressed 
                        x-large 
                        :disabled="!code || code.length !== 4 || loading" 
                        :loading="loading" 
                        color="#D4780A" 
                        class="verify-btn white--text mb-8" 
                        @click.prevent="verifyEmailAction"
                    >
                        Verify Email
                    </v-btn>

                    <!-- Resend Code -->
                    <p class="resend-text">
                        <span class="didnt-get-code">Didn't get the code? </span>
                        <a href="#" class="resend-link" :class="{ 'disabled': sendLoading }" @click.prevent="sendCodeAgain">
                            {{ sendLoading ? 'Sending...' : 'Resend' }}
                        </a>
                    </p>
                </v-form>
            </v-card>
        </v-container>

        <!-- Change Email Dialog -->
        <v-dialog v-model="showChangeEmailDialog" max-width="500px" persistent>
            <v-card class="change-email-dialog">
                <v-card-title class="dialog-title">
                    <span>Change Your Email</span>
                    <v-spacer></v-spacer>
                    <v-btn icon small @click="closeChangeEmailDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                
                <v-card-text class="pt-6">
                    <p class="dialog-subtitle mb-6">
                        Enter your new email address. We'll send a verification code to this new email.
                    </p>
                    
                    <v-form ref="changeEmailForm">
                        <v-text-field
                            v-model="newEmail"
                            label="New Email Address"
                            type="email"
                            outlined
                            :error-messages="emailError"
                            :disabled="changeEmailLoading"
                            @input="emailError = ''"
                            @keyup.enter="changeEmail"
                            placeholder="example@email.com"
                            class="email-input"
                        ></v-text-field>
                    </v-form>
                </v-card-text>
                
                <v-card-actions class="px-6 pb-6">
                    <v-btn
                        text
                        @click="closeChangeEmailDialog"
                        :disabled="changeEmailLoading"
                        class="cancel-btn"
                    >
                        Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="#D4780A"
                        depressed
                        :loading="changeEmailLoading"
                        :disabled="!newEmail || changeEmailLoading"
                        @click="changeEmail"
                        class="submit-btn white--text"
                    >
                        Update Email
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>

// @ is an alias to /src
import digitsInput from '../components/default/digits-input'
export default {
    name: 'Confirm',
    data(){
        return {
            loading: false,
            sendLoading: false,
            code: '',
            showChangeEmailDialog: false,
            newEmail: '',
            emailError: '',
            changeEmailLoading: false,
        };
    },
    components: {
        digitsInput
    },
    computed:{
        appLoadedOnce(){
            return this.$store.state.loaded;
        },
        user(){
            return this.$store.state.userModule.user;
        },
    },
    methods: {
        verifyEmailAction(){
            // const self = this;
            let code = this.code,
                pattern = /^[0-9]{4}$/g;
            // check if reponse if parseInt for code is int, and length = 4
            if (code && pattern.test(code.toString())){
                
                this.loading = true;
                this.$store.dispatch('verifyEmailAction', {code}).then(response=>{
                    this.loading = false;
                    if (response.status === 209){
                        this.showMsg("Email already activated!", 'success');
                    }else{
                        this.showMsg("Email confirmed successfully", 'success');
                    }
                    window.setTimeout(()=>{
                        const u = this.user;
                        if (u.needs_consultation_redirect) {
                            this.$router.push({name: 'consultation-booking'});
                        } else if (u.needs_evaluation_redirect) {
                            this.$router.push({name: 'evaluation-booking'});
                        } else if (!u.interests_completed) {
                            this.$router.push({name: 'interests'});
                        } else {
                            this.$router.push({name: 'home'});
                        }
                    }, 500);
                }).catch(error=>{
                    // this.$refs.codeForm.reset();
                    if (error.response && (error.response.status === 401 || error.response.status === 404 )){
                        this.showMsg("Code is invalid");
                    }else{
                        this.showMsg("Error occurred during activation, try again..");
                    }
                    
                    this.loading = false;
                })
                
            }else{
                this.showMsg("Enter code correctly! (4-digits)");
            }
        },
        sendCodeAgain(){
            this.sendLoading = true;
            this.$store.dispatch('sendCodeAgain').then(()=>{
                this.sendLoading = false;
                this.showMsg("Code sent successfully to your email", 'grey darken-3');
            }).catch(()=>{
                this.showMsg("Error occurred during sending code");
                this.sendLoading = false;
            })
        },
        changeEmail(){
            // Validate email format
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!this.newEmail || !emailPattern.test(this.newEmail)) {
                this.emailError = 'Please enter a valid email address';
                return;
            }

            // Check if it's the same email
            if (this.newEmail.toLowerCase() === this.user.email.toLowerCase()) {
                this.emailError = 'This is already your current email';
                return;
            }

            this.changeEmailLoading = true;
            this.emailError = '';

            this.$store.dispatch('changeEmailForVerification', { email: this.newEmail })
                .then(() => {
                    this.changeEmailLoading = false;
                    this.showChangeEmailDialog = false;
                    this.newEmail = '';
                    this.showMsg("Email updated successfully! A new verification code has been sent.", 'success');
                })
                .catch(error => {
                    this.changeEmailLoading = false;
                    
                    if (error.response) {
                        if (error.response.status === 403) {
                            this.emailError = 'Your email is already verified';
                        } else if (error.response.status === 422) {
                            // Validation error
                            const errors = error.response.data.errors;
                            if (errors && errors.email) {
                                this.emailError = errors.email[0];
                            } else {
                                this.emailError = 'This email is already in use';
                            }
                        } else {
                            this.showMsg("Error occurred while updating email");
                        }
                    } else {
                        this.showMsg("Error occurred while updating email");
                    }
                });
        },
        closeChangeEmailDialog(){
            this.showChangeEmailDialog = false;
            this.newEmail = '';
            this.emailError = '';
        },
        updateCode(code){
            this.code = code;
        },
        showMsg(text, color = 'error', timeout = 4000){
            this.$store.dispatch('addSnackMsg', {show: true, text: text, color: color, timeout: timeout, msgBar: true});
        },
        closeMsg(){
            if (this.$store.state.globalModule.snackMsg.text){
                this.$store.dispatch('addSnackMsg', {show: false, text: '', msgBar: true});
            }
        },
        logout(){
            this.$store.dispatch('logout');
        },
        goBack(){
            this.$router.go(-1);
            // this.$router.push({path: '/'});
        },
    },
    watch: {
        code(){
            // this.closeMsg();
        }
    },
    mounted(){
        // redirect if already confirmed
        if (this.user.email_verified_at){
            this.showMsg("Account is already confirmed", 'grey darken-3');
            this.$router.push({name: 'home'});
        }
    }
}
</script>

<style lang="scss" scoped>
    .page {
        background-color: #FAFAFA;
    }

    .form-wrapper {
        width: 100%;
        max-width: 600px;
        background: white;
        border-radius: 16px;
        
        @media (min-width: 960px) {
            width: 600px;
        }
    }

    .lock-icon-wrapper {
        display: flex;
        justify-content: center;
    }

    .lock-icon-circle {
        width: 120px;
        height: 120px;
        border-radius: 50%;
        background: linear-gradient(135deg, #D4780A 0%, #E89A3C 100%);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .confirm-title {
        font-family: 'Inter', sans-serif;
        font-size: 32px;
        font-weight: 700;
        color: #1E1E1E;
        line-height: 1.2;
    }

    .confirm-subtitle {
        font-family: 'Inter', sans-serif;
        font-size: 16px;
        font-weight: 400;
        color: #707070;
        line-height: 1.6;
    }

    .user-email {
        color: #1E1E1E;
        font-weight: 600;
    }

    .not-your-email {
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        color: #707070;
    }

    .change-email-link {
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        color: #D4780A;
        text-decoration: underline;
        font-weight: 500;
        
        &:hover {
            color: #B86509;
        }
    }

    .verify-btn {
        height: 56px !important;
        border-radius: 12px !important;
        font-family: 'Montserrat', sans-serif;
        font-size: 16px;
        font-weight: 600;
        letter-spacing: 0.5px;
        text-transform: none;
    }

    .resend-text {
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        margin: 0;
    }

    .didnt-get-code {
        color: #707070;
    }

    .resend-link {
        color: #D4780A;
        text-decoration: underline;
        font-weight: 500;
        
        &:hover {
            color: #B86509;
        }
        
        &.disabled {
            opacity: 0.5;
            pointer-events: none;
        }
    }

    // Dialog Styles
    .change-email-dialog {
        border-radius: 16px !important;
    }

    .dialog-title {
        font-family: 'Inter', sans-serif;
        font-size: 24px;
        font-weight: 700;
        color: #1E1E1E;
        padding: 24px 24px 16px 24px;
    }

    .dialog-subtitle {
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        color: #707070;
        line-height: 1.6;
    }

    .email-input {
        font-family: 'Inter', sans-serif;
        
        ::v-deep .v-text-field__slot input {
            font-size: 16px;
        }
        
        ::v-deep .v-label {
            font-size: 14px;
        }
    }

    .cancel-btn {
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        font-weight: 500;
        text-transform: none;
        color: #707070;
    }

    .submit-btn {
        font-family: 'Montserrat', sans-serif;
        font-size: 14px;
        font-weight: 600;
        text-transform: none;
        letter-spacing: 0.5px;
        padding: 0 24px !important;
        height: 40px !important;
        border-radius: 8px !important;
    }
</style>